/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./PageCollectionView',
	'jquery',
	'../../../../lib/@waca/core-client/js/core-client/utils/BidiUtil',
	'../../../../app/nls/StringResources',
	'../../../../lib/@waca/core-client/js/core-client/utils/dom-utils',
	'../../../../lib/@waca/core-client/js/core-client/utils/Deferred',
	'../../../../app/ui/dnd/DnDHelper',
	'../../../../app/ui/dialogs/GenericViewDialog',
	'../../../views/LayoutPickerView',
	'../../../../lib/@waca/core-client/js/core-client/utils/EventHelper'
],
function(BaseClass, $, BidiUtil, stringResources, utils, Deferred, DnDHelper, Dialog, TemplatePickerView) {

	let Tab = null;
	Tab = BaseClass.extend( {

		init: function(options) {
			Tab.inherited('init', this, arguments);
			options = options || {};

			this._getLabelId = this.consumeView._getLabelId.bind(this.consumeView);
			this.specializeConsumeView(['showView', 'renderContent', '_attachView', 'onAddClick', 'onReorder', '_handleKeydown', 'showToolbar', 'deleteView', 'updateViewLabel', 'editViewTitle']);
			this.services = options.services;
			this.config = options.config;

			this.layoutsPath = options.layoutsPath;
			this.templateListing = options.templateListing;
			this.whenIsReadyDfd = new Deferred();
			this.backgroundChangeHandle = this.model.on('change:css', () => this.consumeView.tabWidget.updateTheme());
			this.dashboardApi = options.dashboardApi;
			this._attachedHandles = {};

			this._icons = this.dashboardApi.getFeature('Icons');
		},

		renderContent: function() {
			return this.overridden.renderContent().then(() => {
				this.consumeView.tabWidget.setState({ addable: true, deleteable: this.allowTabDeletion(), draggable: true });
				if(this.model.items && this.model.items.length) {
					this.model.items.forEach(item => this._attachView(item));
				}
				this._initializeDnd();
			});
		},

		whenIsReady: function() {
			return this.whenIsReadyDfd.promise;
		},

		destroy: function() {
			// Remove the registered dropZonez
			if (this._tabStripDropZone){
				this._tabStripDropZone.remove();
			}

			if (this._tabDropHandler){
				this._tabDropHandler.deregister();
				this._tabDropHandler = null;
			}

			if (this.backgroundChangeHandle) {
				this.backgroundChangeHandle.remove();
			}

			if (this.toolbar && this.toolbar.remove) {
				this.toolbar.remove();
			}
			this.toolbar = null;

			this.model.items.forEach(this._detachView.bind(this));
			this.consumeView.updateState({ addable: false, deleteable: false, draggable: false });
			Tab.inherited('destroy', this, arguments);
		},

		_initializeDnd: function() {
			this._dndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			this.setupGlobalDropZone();
			this.whenIsReadyDfd.resolve();
		},

		_attachView:function(pageModel){
			this.services.getSvcSync('TranslationService').registerView({ view: this, model: pageModel });
			this._attachedHandles[pageModel.id] = pageModel.on('change:title', this.onTabLabelChange.bind(this, pageModel), this);
			this._attachedHandles[pageModel.id] = pageModel.on('change:tabTextColor', (options) => this.consumeView.tabWidget.updateTheme(options));
			this._attachedHandles[pageModel.id] = pageModel.on('change:tabSelectedLineColor', (options) => this.consumeView.tabWidget.updateTheme(options));
			this._attachedHandles[pageModel.id] = pageModel.on('change:tabBackgroundColor', (options) => this.consumeView.tabWidget.updateTheme(options));
			this._attachedHandles[pageModel.id] = pageModel.on('change:tabIconColor',(options) => this.consumeView.tabWidget.updateTheme(options));
			this._attachedHandles[pageModel.id] = pageModel.on('change:tabIcon', (options) => this.onTabIconChange(pageModel, options));
		},

		_detachView:function(pageModel){
			if (this._attachedHandles[pageModel.id]) {
				this._attachedHandles[pageModel.id].remove();
				delete this._attachedHandles[pageModel.id];
			}
		},

		addTranslationIcon: function (multilingualAttributesInfo) {
			const pageModel = multilingualAttributesInfo && multilingualAttributesInfo[0].propertyParentModel;
			if (this.model.get('hideTab')) {
				return 0;
			} else {
				return this.consumeView.tabWidget.updateTranslationIcon(pageModel, true);
			}
		},

		removeTranslationIcon: function (multilingualAttributesInfo) {
			const pageModel = multilingualAttributesInfo && multilingualAttributesInfo[0].propertyParentModel;
			return this.consumeView.tabWidget.updateTranslationIcon(pageModel, false);
		},

		_handleKeydown:function(pageModel, evt, node){
			const keyCode = evt.keyCode ;
			if (this._shouldShowToolbar(evt, keyCode)){
				this.showToolbar(pageModel, node);
				evt.preventDefault();
				return false; //Signal to the browser that it should prevent the default behavior of opening the context menu
			} else {
				this.overridden._handleKeydown(pageModel, evt, node);
			}
		},

		// Shift + F10 key - show contextual toolbar
		_shouldShowToolbar: function(evt, keyCode) {
			return evt.shiftKey && keyCode === 121;
		},

		/**
		 * Move the view to the location before the provided id.
		 * @param pageModel
		 * @param beforeId
		 */
		moveViewBefore:function(pageModel, beforeId){
			// Avoid a model update when it is not needed
			if (pageModel.id !== beforeId && pageModel.getNextSiblingId() !== beforeId){
				pageModel.updateModel({ updateArray:[{
					id: pageModel.id,
					parentId: pageModel.getParent().id,
					insertBefore:beforeId
				}] });
			}
		},

		showView: function(viewModel) {
			if (this.isToolbarShown) {
				this.hideToolbar();
			}

			//call consume view's showView
			this.overridden.showView(viewModel);
		},

		showToolbar: function(viewModel, node){
			if (!this.isToolbarShown) {
				this.isToolbarShown = true;
				this.toolbar.clearItems();

				const items = this._toolbarItems(viewModel);
				this.toolbar.addItems(items);

				this.toolbar.setSelectionContext($(node));
				this.toolbar.show();
			} else {
				this.hideToolbar();
			}
		},

		hideToolbar: function(){
			this.isToolbarShown = false;
			return this.toolbar.hide();
		},

		_toolbarItems: function(viewModel) {
			let items = [];
			/*
			 * By UX design order of icons matters:
			 * 1. edit
			 * 2. duplicate
			 * 3. delete (if more then one tab exists)
			 * 4. change template
			 */
			items.push({
				icon: this._icons.getIcon('edit').id,
				label: stringResources.get('toolbarActionEditTitle'),
				name: 'rename',
				type: 'Button',
				action: this.editViewTitle.bind(this, viewModel)
			});

			items.push({
				icon: this._icons.getIcon('dashboard-duplicate').id,
				label: stringResources.get('duplicate'),
				name: 'duplicate',
				type: 'Button',
				action: this.duplicateTab.bind(this, viewModel.id)
			});

			if(this.config.changeTabTemplate !== 'disabled') {
				items.push({
					icon: this._icons.getIcon('template').id,
					label: stringResources.get('changeTemplate'),
					name: 'changeTemplate',
					type: 'Button',
					disabled: viewModel.templateName === 'NoTemplate',
					action: this.selectTemplate.bind(this, viewModel.id)
				});
			}

			return items;
		},

		/**
		 * Called when a new tab is added.
		 *
		 * @param n - tab content node
		 * @param insertBefore - content node to insert before, null if we're creating a new tab, and a string when we're dragging tabs around
		 * @param model - model of the page being added
		 */
		add: function(layoutView, insertBefore) {
			const model = layoutView.model;
			const tabs = this.consumeView.tabWidget.state.tabs;
			let layoutIndex, isNew = true;
			for (let i = 0; i < tabs.length; i++) {
				if (this.consumeView._getModelIdFromPanel(tabs[i].props.id) === model.id) {
					isNew = false;
					layoutIndex = i;
					break;
				}
			}

			if (!isNew) {
				// This is a re-order. See if we need to update the state.
				if (!((insertBefore === null && layoutIndex === tabs.length - 1) || (tabs[layoutIndex + 1] && this.consumeView._getModelIdFromPanel(tabs[layoutIndex + 1].props.id) === insertBefore))) {
					let updatedTabs = [];
					tabs.forEach((tab) => {
						if (this.consumeView._getModelIdFromPanel(tab.props.id) === insertBefore) {
							updatedTabs.push(tabs[layoutIndex]);
							updatedTabs.push(tab);
						} else if (this.consumeView._getModelIdFromPanel(tab.props.id) !== model.id) {
							updatedTabs.push(tab);
						}
					});
					if (insertBefore === null) {
						updatedTabs.push(tabs[layoutIndex]);
					}
					this.consumeView.tabWidget.setState({ tabs: updatedTabs });
				}
				const $tabContent = this.$el.find('#' + this._getViewId(model));
				$tabContent.append(layoutView.domNode);
			} else {
				const $tabContent = $(this.htmlTemplate.replaceLayoutValues(this.htmlTemplate.getItemTemplate('tab', '', model), this.model));
				$tabContent.append(layoutView.domNode);
				this.$el.find('.tabPageHolder').append($tabContent);

				let updatedTabs = [];
				const newTab = this.consumeView._createTabPanel(model, $tabContent, this.consumeView._getColorProperties());
				if (insertBefore) {
					tabs.forEach((tab) => {
						if (this.consumeView._getModelIdFromPanel(tab.props.id) === insertBefore) {
							updatedTabs.push(newTab);
						}
						updatedTabs.push(tab);
					});
				} else {
					updatedTabs = tabs.slice();
					updatedTabs.push(newTab);
				}
				this.consumeView.tabWidget.setState({ tabs: updatedTabs });
				this.consumeView.canvas.selectContent([model.id]);

				// Attach the tab behavior (e.g. events)
				this.overridden._attachView(model);
				this._attachView(model);
			}
			if (this.consumeView.tabWidget.state.deleteable===false && this.allowTabDeletion()) {
				this.consumeView.tabWidget.setState({ deleteable: true });
			}
		},

		allowTabDeletion: function() {
			return this.model.items.length > 1;
		},

		_getDefaultViewLabel: function() {
			const tabLength = this.model.items.length;
			for (let i=1; i<tabLength+1; i++) {
				let isAvailable = true;
				for (let j = 0; j < tabLength; j++) {
					if (this.model.items[j].get('title') === stringResources.get('defaultTabTitle', { index: i })) {
						isAvailable = false;
						break;
					}
				}
				if (isAvailable) {
					return stringResources.get('defaultTabTitle', { index: i });
				}
			}
			return stringResources.get('defaultTabTitle', { index: tabLength + 1 });
		},

		onAddClick: function(){
			return this._invokeLifeCycleHandlers('tabs.onPressNew', {
				$el: this.$el
			}).then(function () {
				//default behavior goes in here.
				this._openTemplateSelectDialog(); //TODO this needs to be triggered via a lifecycle handler!
			}.bind(this));
		},

		onReorder: function(tabsList, newIndex, tabId) {
			let beforeId = null;
			if ((newIndex + 1) < tabsList.length) {
				beforeId = this.consumeView._getModelIdFromPanel(tabsList[newIndex + 1].props.id);
			}
			let movedId = this.consumeView._getModelIdFromPanel(tabId);
			this.moveViewBefore(this.model.findModel(movedId), beforeId);
			this.consumeView.eventRouter.trigger('properties:refreshPane');
		},


		onTabLabelChange: function(pageModel) {
			this.consumeView.tabWidget.updateTabLabel(pageModel);
			const pageIndex = this.model.items.findIndex(item => item.id === pageModel.id);
			let propertyName = 'tabIndividualStylingLink' + pageIndex;
			const updatePayload = {
				propertyName: propertyName,
				subPropertyName: 'r_' + propertyName + '0',
				value: pageModel.get('title')
			};
			this.consumeView.eventRouter.trigger('properties:setValue', updatePayload);
			this.consumeView.eventRouter.trigger('properties:setChildValue', {
				propertyName: 'title',
				value: pageModel.get('title'),
				contentId: pageModel.id
			});
		},

		onTabIconChange: function(pageModel, options) {
			this.consumeView.tabWidget.updateTabIcon(pageModel);
			if (options && options.sender === 'UndoRedoController') {
				const content = this.consumeView.canvas.getContent(pageModel.id);
				this.consumeView.eventRouter.trigger('properties:refreshChild', { content });
				this.consumeView.eventRouter.trigger('properties:refreshPane');
			}
		},

		setupGlobalDropZone: function() {
			if (this._tabStripDropZone){
				this._tabStripDropZone.remove();
			}

			this._tabStripDropZone = this._dndManager.addDropTarget($(this.consumeView.tabWidget.tabListNode), '[role=tab]', {
				accepts: dragObject => {
					if (dragObject.type === 'widget') {
						return true;
					}
					return false;
				},
				onDragEnter: (dragObject, node) => {
					const tabPanel = this.consumeView._getPanelFromNode(node);
					const hoverColor = tabPanel.props.hoverColor;
					$(node).css('background-color', hoverColor);
				},
				onDragLeave: (dragObject, node) => {
					const tabPanel = this.consumeView._getPanelFromNode(node);
					const backgroundColor = tabPanel.props.backgroundColor;
					$(node).css('background-color', backgroundColor);
				},
				onDrop: (dragObject, node) => {
					const tabPanel = this.consumeView._getPanelFromNode(node);
					if(dragObject.type === 'widget' && tabPanel) {
						const id = this.consumeView._getModelIdFromPanel(tabPanel.props.id);
						DnDHelper.handleWidgetDrop(this.consumeView.canvas, id, dragObject);
					}
				},
				info: (node) => {
					const tabPanel = this.consumeView._getPanelFromNode(node);
					if(tabPanel) {
						return {
							containerId: this.consumeView._getModelIdFromPanel(tabPanel.props.id)
						};
					}
					return {};
				},
				type: 'dropOnTab'
			});

			if (!this._tabDropHandler) {
				this._tabDropHandler = this._dndManager.registerDropHandler({
					accepts: (dragPayload, dropPayload) => {
						return dragPayload.type.match(/content\..*/) && dropPayload.type === 'dropOnTab';
					},
					onDrop: (dragPayload, dropPayload) => {
						if (dropPayload.info.containerId) {
							const content = {
								...dragPayload.data,
								containerId: dropPayload.info.containerId
							};
							this.consumeView.canvas.addContent(content);
						}
					}
				});
			}
		},

		updateViewLabel: function(pageModel, sText) {
			const content = this.consumeView.canvas.getContent(pageModel.id);
			content.setPropertyValue('title', sText);
		},

		duplicateTab: function(sViewId) {
			this.model.boardModel.duplicateLayout(sViewId);
		},

		deleteView: function(sViewId){
			this.consumeView.canvas.removeContent(sViewId);
			this.services.getSvcSync('TranslationService').deregisterView(sViewId);
		},

		selectTemplate: function(sViewId) {
			this._openTemplateSelectDialog(sViewId, true);
		},

		/**
		 * Private method that creates the template selection dialog options
		 * It gets the excluded tab templates list from the perspective collection and adds the default exclusion NoTemplate to the list
		 * Returns template selection dialog options
		 */
		_templateSelectDialogViewOptions: function() {
			let excludedTabTemplates = this.dashboardApi.getCanvas().getPropertyValue('excludedTemplates');
			excludedTabTemplates.push('NoTemplate');
			const dialogElement = document.createElement('div');
			dialogElement.classList.add('templatePickerDialog');

			return {
				el: dialogElement,
				templateListing: this.templateListing,
				layoutsPath: this.layoutsPath,
				disableHeaderSection: true,
				action: function() {},
				// Freeform no longer is diplayed as an template option - replaced by the 'absolute' dashboard property
				excludeTemplates: excludedTabTemplates
			};
		},

		/**
		 * @param viewId string a model id for the view (of the tab) to update
		 * @param forEdit boolean true if opening the template select dialog for editing; undefined/false for new
		 */
		_openTemplateSelectDialog: function(viewId, forEdit) {
			let templateSelectDialog;
			let templateView;
			let action;

			let viewOptions = this._templateSelectDialogViewOptions();
			if(!forEdit) {
				action = ({ layout }) => {
					this._addBoardLayout(layout);
					this._setDefaultLayoutPositioning();
					this.lastSelectedTemplate = layout.templateName;
					templateSelectDialog.hide();
				};
			} else if(forEdit === true) {
				/*
				* When we are editing we need to use a slightly different action
				*/
				action = ({ layout }) => {
					this._updateTemplateDropZones(viewId, layout);
					this.lastSelectedTemplate = layout.templateName;
					templateSelectDialog.hide();
				};
			}

			viewOptions.action = () => {
				return templateView.getSelectedLayoutSpec().then(action);
			};

			templateSelectDialog = new Dialog({
				title: stringResources.get('selectTemplateLabel'),
				viewClass: TemplatePickerView,
				viewOptions: viewOptions,
				okCallback: viewOptions.action,
				buttons: [
					{
						text: stringResources.get('dlg_create'),
						handler: viewOptions.action,
						type: 'primary'
					},
					'cancel'
				],
				showCloseX: true,
				width: '850px'
			});
			templateView = templateSelectDialog.view;

			templateView.setNavigationTemplate('tab');
			const selectedTemplate = this.lastSelectedTemplate || (this.model.items.length > 0 && this.model.items[0].templateName) || 'NoTemplate';
			templateView.selectLayout(selectedTemplate);

			templateSelectDialog.open();
		},

		_setDefaultLayoutPositioning: function() {
			let defaultLayoutPositioning = this.model.getValueFromSelfOrParent('layoutPositioning') || 'relative';

			let layouts = this.model.findDescendantsWithType('genericPage');
			for (let layout of layouts) {
				layout.layoutPositioning = defaultLayoutPositioning;
			}
		},

		editViewTitle:function(viewModel){
			this.hideToolbar().then(()=>{
				this.consumeView.tabWidget.editTabLabel(true, viewModel);
			});
		},

		_addBoardLayout: function(layout) {
			layout = layout || { type:'absolute' };
			layout.title = this._getDefaultViewLabel();
			layout.localizedProps = ['title'];

			const content = {
				spec: layout,
				containerId: this.model.id
			};
			this.consumeView.canvas.addContent(content);
		},

		/**
		 * Private method to handle updating the drop zones in dashboards
		 * After the user selects a template we update the layout drop zones
		 *
		 * @param viewId string The model id of the dashboard tab the user is updating
		 * @param layout object The user selected layout to update/change the template to
		 */
		_updateTemplateDropZones: function(viewId, layout) {
			this.model.boardModel.updateLayoutDropZones({
				id: viewId,
				model: layout
			});
		},

		/**
		 * Called when a view is removed
		 *
		 * @param node
		 */
		removeChild: function(layoutView){
			if(!layoutView) {
				return;
			}

			// detach model event listener added by the current parent
			layoutView.model.off('change:title');

			const model = layoutView.model;
			const tabs = this.consumeView.tabWidget.state.tabs;
			let isPresent = false;
			let updatedTabs = [], index;
			tabs.forEach((tab, i) => {
				if (this.consumeView._getModelIdFromPanel(tab.props.id) === model.id) {
					isPresent = true;
					index = i;
				} else {
					updatedTabs.push(tab);
				}
			});

			if (isPresent) {
				let stateUpdate = {
					tabs: updatedTabs
				};
				if (this.consumeView._getViewId(model) === this.consumeView._currentViewId) { //We are deleting the selected tab. We need to select a new one.
					const selectedIndex = Math.max(index-1, 0);
					stateUpdate.selectedTab = this.consumeView._getPanelId(this.model.items[selectedIndex].id);
				}

				this.consumeView.tabWidget.setState(stateUpdate);
			}

			//Remove the dom node
			let parentSelector = '#' + layoutView.domNode.parentNode.id;
			$(parentSelector).remove();

			Tab.inherited('removeChild', this, arguments);

			if (this.consumeView.tabWidget.state.deleteable===true && !this.allowTabDeletion()) {
				this.consumeView.tabWidget.setState({ deleteable: false });
			}

		},

		_invokeLifeCycleHandlers: function (name, payload) {
			return this.services.getSvc('.LifeCycleManager')
				.then(function (LifeCycleManager) {
					return LifeCycleManager.invokeLifeCycleHandlers(name, payload);
				});
		}

	});

	return Tab;
});
